import { useMemo, useState, type FormEvent } from 'react';
import { Button, ErrorNotice, Field, SimulatedTag } from '@hive/ui';
import { api, errorMessage, type Role, type RoleInfo, type UserAccount, type Venue } from '../api/client';
import { Dialog } from './Dialog';

interface Props {
  /** The user to edit, or undefined to add one. */
  user?: UserAccount;
  roles: RoleInfo[];
  venues: Venue[];
  /** The signed-in admin, who cannot disable, demote or remove themselves. */
  selfId?: string;
  onClose(): void;
  onSaved(): void;
}

/** Add or edit a user: details, role, venues, password, disable and remove. */
export function UserDialog({ user, roles, venues, selfId, onClose, onSaved }: Props) {
  const editing = Boolean(user);
  const self = user?.id === selfId;
  const [name, setName] = useState(user?.name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [title, setTitle] = useState(user?.title ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'venue_staff');
  const [venueIds, setVenueIds] = useState<string[]>(user?.venueIds ?? []);
  const [password, setPassword] = useState('');
  const [venueQuery, setVenueQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const roleInfo = roles.find((r) => r.role === role);
  const scoped = roleInfo ? !roleInfo.allVenues : true;
  const shown = useMemo(() => {
    const q = venueQuery.trim().toLowerCase();
    const list = q ? venues.filter((v) => [v.name, v.code, v.city].some((x) => x?.toLowerCase().includes(q))) : venues;
    // Real venues first, then selected ones, then the rest.
    return [...list].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
    function rank(v: Venue) {
      return (v.source === 'simulated' ? 2 : 0) + (venueIds.includes(v.id) ? 0 : 1);
    }
  }, [venues, venueQuery, venueIds]);

  const toggleVenue = (id: string) => setVenueIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (scoped && venueIds.length === 0) return setError('Choose at least one venue for venue staff.');
    setBusy(true);
    const result = user
      ? await api.PATCH('/v1/users/{userId}', {
          params: { path: { userId: user.id } },
          body: { name, title, email, role, ...(scoped ? { venueIds } : {}) },
        })
      : await api.POST('/v1/users', {
          body: {
            username: username.trim().toLowerCase(),
            name,
            role,
            password,
            ...(title ? { title } : {}),
            ...(email ? { email } : {}),
            ...(scoped ? { venueIds } : {}),
          },
        });
    setBusy(false);
    if (result.error) return setError(errorMessage(result.error));
    onSaved();
    onClose();
  }

  async function setDisabled(disabled: boolean) {
    if (!user) return;
    setBusy(true);
    const { error: err } = await api.PATCH('/v1/users/{userId}', { params: { path: { userId: user.id } }, body: { disabled } });
    setBusy(false);
    if (err) return setError(errorMessage(err));
    onSaved();
    onClose();
  }

  async function remove() {
    if (!user) return;
    setBusy(true);
    const { error: err } = await api.DELETE('/v1/users/{userId}', { params: { path: { userId: user.id } } });
    setBusy(false);
    if (err) return setError(errorMessage(err));
    onSaved();
    onClose();
  }

  async function resetPassword() {
    if (!user) return;
    if (password.length < 8) return setError('Use at least 8 characters for the new password.');
    setBusy(true);
    const { error: err, response } = await api.POST('/v1/users/{userId}/password', { params: { path: { userId: user.id } }, body: { password } });
    setBusy(false);
    if (response.status !== 204) return setError(errorMessage(err));
    setPassword('');
    setNotice(`New password set for ${user.name}. Share it with them securely.`);
    onSaved();
  }

  return (
    <Dialog
      titleId="user-title"
      title={editing ? user!.name : 'Add a user'}
      subtitle={editing ? `${user!.username}${user!.disabled ? ' · disabled' : ''}` : 'They sign in to Keeper, or master control if their role allows it.'}
      onClose={onClose}
      width={620}
      footer={
        confirmRemove ? (
          <>
            <span className="mc-grow mc-small">Remove {user?.name}? This cannot be undone.</span>
            <Button onClick={() => setConfirmRemove(false)}>Keep</Button>
            <Button variant="primary" className="mc-btn-danger" onClick={remove} disabled={busy}>
              Remove user
            </Button>
          </>
        ) : (
          <>
            {editing && !self && (
              <span className="mc-grow mc-dialog__side">
                <Button variant="link" onClick={() => setDisabled(!user!.disabled)} disabled={busy}>
                  {user!.disabled ? 'Re-enable' : 'Disable'}
                </Button>
                <Button variant="link" className="mc-link-danger" onClick={() => setConfirmRemove(true)} disabled={busy}>
                  Remove
                </Button>
              </span>
            )}
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" form="user-form" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add user'}
            </Button>
          </>
        )
      }
    >
      <form id="user-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        {notice && <p className="mc-note mc-note--ok">{notice}</p>}
        <div className="mc-grid2">
          <Field id="u-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          {editing ? (
            <Field id="u-username" label="Username" value={username} disabled readOnly />
          ) : (
            <Field
              id="u-username"
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              pattern="[a-z0-9][a-z0-9._\-]{1,62}"
              title="Lower case letters, digits, dot, dash and underscore"
              autoComplete="off"
            />
          )}
          <Field id="u-title" label="Job title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          <Field id="u-email" label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />
        </div>

        <fieldset className="mc-fieldset">
          <legend className="hv-field__label">Role</legend>
          {self && <p className="mc-small hv-muted">You cannot change your own role.</p>}
          <div className="mc-roles">
            {roles.map((r) => (
              <label key={r.role} className={`mc-role${role === r.role ? ' mc-role--on' : ''}`}>
                <input type="radio" name="role" value={r.role} checked={role === r.role} disabled={self} onChange={() => setRole(r.role)} />
                <span className="mc-strong">{r.label}</span>
                <span className="mc-small hv-muted">{r.description}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {scoped ? (
          <fieldset className="mc-fieldset">
            <legend className="hv-field__label">
              Venues <span className="hv-muted">· {venueIds.length} selected</span>
            </legend>
            <label className="mc-search__box mc-search__box--full">
              <span className="hv-sr-only">Find a venue</span>
              <input type="search" placeholder="Find a venue by name, ID or city" value={venueQuery} onChange={(e) => setVenueQuery(e.target.value)} />
            </label>
            <ul className="mc-checklist">
              {shown.map((v) => (
                <li key={v.id}>
                  <label>
                    <input type="checkbox" checked={venueIds.includes(v.id)} onChange={() => toggleVenue(v.id)} />
                    <span className="mc-grow">{v.name}</span>
                    <span className="mc-small hv-muted">{[v.code, v.city].filter(Boolean).join(' · ')}</span>
                    {v.source === 'simulated' && <SimulatedTag title="Simulated venue" />}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ) : (
          <p className="mc-note mc-note--info">{roleInfo?.label ?? 'This role'} covers every venue in the tenant.</p>
        )}

        {editing ? (
          <fieldset className="mc-fieldset">
            <legend className="hv-field__label">Set a new password</legend>
            <div className="mc-inline">
              <input
                id="u-password"
                aria-label="New password"
                className="hv-input mc-grow"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button onClick={resetPassword} disabled={busy || !password}>
                Set password
              </Button>
            </div>
          </fieldset>
        ) : (
          <Field
            id="u-password"
            label="Initial password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            placeholder="At least 8 characters; they can change it after signing in"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}
      </form>
    </Dialog>
  );
}
